import axios, { AxiosInstance } from "axios";
import { AnsweringClient } from "../clients/answeringClient";
import { environment } from "../utils/environment";

export type DiscountType = "percent" | "fixed_amount";
export type CouponCodeUsageType = "global" | "user";

interface CouponValueFields {
  percentOff: number | null;
  amountOffCents: number | null;
  minOrderAmountCents: number | null;
  startsAt: string | null;
  endsAt: string | null;
}

export interface CouponListItem extends CouponValueFields {
  id: string;
  internalName: string;
  discountType: DiscountType;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CouponDetail extends CouponListItem {}

export interface PaginatedCoupons {
  data: CouponListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface ListCouponsParams {
  internalName?: string;
  active?: boolean;
  page: number;
  limit: number;
}

export interface CreateCouponPayload {
  internalName: string;
  discountType: DiscountType;
  percentOff?: number | null;
  amountOffCents?: number | null;
  minOrderAmountCents?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  active?: boolean;
}

export interface UpdateCouponPayload {
  internalName?: string;
  discountType?: DiscountType;
  percentOff?: number | null;
  amountOffCents?: number | null;
  minOrderAmountCents?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  active?: boolean;
}

export interface CouponCodeAssignedUser {
  uid: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface CouponCodeAssignedInstrument {
  id: number;
  title: string;
}

export interface CouponCodeListItem {
  id: string;
  couponId: string;
  code: string;
  assignedUserUid: string[] | null;
  assignedInstrumentId: number[] | null;
  assignedUsers: CouponCodeAssignedUser[] | null;
  assignedInstruments: CouponCodeAssignedInstrument[] | null;
  usageLimit: number | null;
  usageType: CouponCodeUsageType;
  redemptionCount: number;
  startsAt: string | null;
  endsAt: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedCouponCodes {
  data: CouponCodeListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface ListCouponCodesParams {
  couponId: string;
  page?: number;
  limit?: number;
  search?: string;
}

export interface CreateCouponCodePayload {
  couponId: string;
  code: string;
  assignedUserUid?: string[] | null;
  assignedInstrumentId?: number[] | null;
  usageLimit?: number | null;
  usageType: CouponCodeUsageType;
  startsAt?: string | null;
  endsAt?: string | null;
  active?: boolean;
}

export interface UpdateCouponCodePayload {
  couponId?: string;
  code?: string;
  assignedUserUid?: string[] | null;
  assignedInstrumentId?: number[] | null;
  usageLimit?: number | null;
  usageType?: CouponCodeUsageType;
  startsAt?: string | null;
  endsAt?: string | null;
  active?: boolean;
}

export interface ValidateCouponCodePayload {
  code: string;
  orderAmountCents: number;
  cartId: number;
}

export interface ValidatedCouponCode {
  couponId: string;
  couponCodeId: string;
  code: string;
  discountType: DiscountType;
  percentOff: number | null;
  amountOffCents: number | null;
  minOrderAmountCents: number | null;
  assignedUserUid: string[] | null;
  couponStartsAt: string | null;
  couponEndsAt: string | null;
  codeStartsAt: string | null;
  codeEndsAt: string | null;
}

export interface CouponsControllerDelegate {
  didFetchCoupons(payload: PaginatedCoupons): void;
  didFailToFetchCoupons(message?: string): void;
  didFetchCoupon(coupon: CouponDetail): void;
  didFailToFetchCoupon(message?: string): void;
  didCreateCoupon(coupon: CouponListItem): void;
  didFailToCreateCoupon(message?: string): void;
  didUpdateCoupon(coupon: CouponListItem): void;
  didFailToUpdateCoupon(message?: string): void;
  didDeleteCoupon(couponId: string): void;
  didFailToDeleteCoupon(message?: string): void;
  shouldLogout(): void;
}

export class CouponsController {
  constructor(
    private readonly delegate: CouponsControllerDelegate,
    private readonly api: AxiosInstance = axios.create({
      baseURL: environment.apiBaseUrl,
    }),
    private readonly answeringClient = new AnsweringClient()
  ) {}

  async listCoupons(
    accessToken: string,
    params: ListCouponsParams
  ): Promise<void> {
    const requestParams = { ...params };
    try {
      const response = await this.withAuth(accessToken, (token) =>
        this.api.get<PaginatedCoupons>("api/v1/admin/coupons", {
          headers: {
            authorization: `Bearer ${token}`,
          },
          params: requestParams,
        })
      );

      const payload: PaginatedCoupons = {
        data: response.data.data.map(this.normalizeCoupon),
        total: response.data.total,
        page: response.data.page,
        limit: response.data.limit,
      };

      this.delegate.didFetchCoupons(payload);
    } catch (error: any) {
      const message = this.extractErrorMessage(
        error,
        "Não foi possível carregar os cupons."
      );
      this.delegate.didFailToFetchCoupons(message);
    }
  }

  async getCoupon(accessToken: string, couponId: string): Promise<void> {
    try {
      const response = await this.withAuth(accessToken, (token) =>
        this.api.get(`api/v1/admin/coupons/${couponId}`, {
          headers: {
            authorization: `Bearer ${token}`,
          },
        })
      );
      const coupon = this.normalizeCouponDetail(response.data);
      this.delegate.didFetchCoupon(coupon);
    } catch (error: any) {
      const message = this.extractErrorMessage(
        error,
        "Não foi possível carregar o cupom."
      );
      this.delegate.didFailToFetchCoupon(message);
    }
  }

  async createCoupon(
    accessToken: string,
    payload: CreateCouponPayload
  ): Promise<void> {
    try {
      const response = await this.withAuth(accessToken, (token) =>
        this.api.post("api/v1/admin/coupons", payload, {
          headers: {
            authorization: `Bearer ${token}`,
          },
        })
      );

      const coupon = this.normalizeCoupon(response.data);
      this.delegate.didCreateCoupon(coupon);
    } catch (error: any) {
      const message = this.extractErrorMessage(
        error,
        "Não foi possível criar o cupom."
      );
      this.delegate.didFailToCreateCoupon(message);
    }
  }

  async updateCoupon(
    accessToken: string,
    couponId: string,
    payload: UpdateCouponPayload
  ): Promise<void> {
    try {
      const response = await this.withAuth(accessToken, (token) =>
        this.api.patch(`api/v1/admin/coupons/${couponId}`, payload, {
          headers: {
            authorization: `Bearer ${token}`,
          },
        })
      );

      const coupon = this.normalizeCoupon(response.data);
      this.delegate.didUpdateCoupon(coupon);
    } catch (error: any) {
      const message = this.extractErrorMessage(
        error,
        "Não foi possível atualizar o cupom."
      );
      this.delegate.didFailToUpdateCoupon(message);
    }
  }

  async deleteCoupon(
    accessToken: string,
    couponId: string
  ): Promise<void> {
    try {
      await this.withAuth(accessToken, (token) =>
        this.api.delete(`api/v1/admin/coupons/${couponId}`, {
          headers: {
            authorization: `Bearer ${token}`,
          },
        })
      );
      this.delegate.didDeleteCoupon(couponId);
    } catch (error: any) {
      const message = this.extractErrorMessage(
        error,
        "Não foi possível excluir o cupom."
      );
      this.delegate.didFailToDeleteCoupon(message);
    }
  }

  async listCouponCodes(
    accessToken: string,
    params: ListCouponCodesParams
  ): Promise<PaginatedCouponCodes> {
    const requestParams = { ...params };
    try {
      const response = await this.withAuth(accessToken, (token) =>
        this.api.get<PaginatedCouponCodes>("api/v1/admin/coupon-codes", {
          headers: {
            authorization: `Bearer ${token}`,
          },
          params: requestParams,
        })
      );

      return {
        data: (response.data.data ?? []).map(this.normalizeCouponCode),
        total: response.data.total ?? 0,
        page: response.data.page ?? requestParams.page ?? 1,
        limit: response.data.limit ?? requestParams.limit ?? 15,
      };
    } catch (error: any) {
      const message = this.extractErrorMessage(
        error,
        "Não foi possível carregar os códigos do cupom."
      );
      throw new Error(message);
    }
  }

  async createCouponCode(
    accessToken: string,
    payload: CreateCouponCodePayload
  ): Promise<CouponCodeListItem> {
    try {
      const response = await this.withAuth(accessToken, (token) =>
        this.api.post("api/v1/admin/coupon-codes", payload, {
          headers: {
            authorization: `Bearer ${token}`,
          },
        })
      );
      return this.normalizeCouponCode(response.data);
    } catch (error: any) {
      const message = this.extractErrorMessage(
        error,
        "Não foi possível criar o código de cupom."
      );
      throw new Error(message);
    }
  }

  async updateCouponCode(
    accessToken: string,
    couponCodeId: string,
    payload: UpdateCouponCodePayload
  ): Promise<CouponCodeListItem> {
    try {
      const response = await this.withAuth(accessToken, (token) =>
        this.api.patch(`api/v1/admin/coupon-codes/${couponCodeId}`, payload, {
          headers: {
            authorization: `Bearer ${token}`,
          },
        })
      );
      return this.normalizeCouponCode(response.data);
    } catch (error: any) {
      const message = this.extractErrorMessage(
        error,
        "Não foi possível atualizar o código de cupom."
      );
      throw new Error(message);
    }
  }

  /**
   * Rejects on every failure path, including a refused refresh: the caller drops
   * the code's row from the table as soon as this resolves.
   */
  async deleteCouponCode(
    accessToken: string,
    couponCodeId: string
  ): Promise<void> {
    try {
      await this.withAuth(accessToken, (token) =>
        this.api.delete(`api/v1/admin/coupon-codes/${couponCodeId}`, {
          headers: {
            authorization: `Bearer ${token}`,
          },
        })
      );
    } catch (error: any) {
      const message = this.extractErrorMessage(
        error,
        "Não foi possível excluir o código de cupom."
      );
      throw new Error(message);
    }
  }

  async validateCouponCode(
    accessToken: string,
    payload: ValidateCouponCodePayload
  ): Promise<ValidatedCouponCode> {
    try {
      const response = await this.withAuth(accessToken, (token) =>
        this.api.post<ValidatedCouponCode>(
          "api/v1/admin/coupon-codes/validate",
          payload,
          {
            headers: {
              authorization: `Bearer ${token}`,
            },
          }
        )
      );
      return response.data;
    } catch (error: any) {
      const message = this.extractErrorMessage(
        error,
        "Não foi possível validar o código de cupom."
      );
      throw new Error(message);
    }
  }

  private normalizeCoupon = (data: any): CouponListItem => {
    const rawActive = data?.active;
    const rawDiscountType = data?.discountType ?? data?.discount_type;

    return {
      id: data?.id ?? "",
      internalName: data?.internalName ?? data?.internal_name ?? "",
      discountType:
        rawDiscountType === "fixed_amount" ? "fixed_amount" : "percent",
      active:
        typeof rawActive === "boolean"
          ? rawActive
          : rawActive === "true" || rawActive === 1,
      createdAt: this.normalizeDate(data?.createdAt ?? data?.created_at),
      updatedAt: this.normalizeDate(data?.updatedAt ?? data?.updated_at),
      ...this.normalizeCouponValueFields(data),
    };
  };

  private normalizeCouponDetail = (data: any): CouponDetail => {
    return this.normalizeCoupon(data);
  };

  private normalizeCouponValueFields = (data: any): CouponValueFields => {
    const rawPercent =
      data?.percentOff !== undefined ? data.percentOff : data?.percent_off;
    const rawAmount =
      data?.amountOffCents !== undefined
        ? data.amountOffCents
        : data?.amount_off_cents;
    const rawMinOrder =
      data?.minOrderAmountCents !== undefined
        ? data.minOrderAmountCents
        : data?.min_order_amount_cents;
    const startsAt = this.normalizeDate(data?.startsAt ?? data?.starts_at);
    const endsAt = this.normalizeDate(data?.endsAt ?? data?.ends_at);
    return {
      percentOff:
        rawPercent === null || rawPercent === undefined
          ? null
          : Number(rawPercent),
      amountOffCents:
        rawAmount === null || rawAmount === undefined
          ? null
          : Number(rawAmount),
      minOrderAmountCents:
        rawMinOrder === null || rawMinOrder === undefined
          ? null
          : Number(rawMinOrder),
      startsAt: startsAt || null,
      endsAt: endsAt || null,
    };
  };

  private normalizeDate(value: unknown): string {
    if (!value) {
      return "";
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    const parsed = new Date(value as string);
    if (Number.isNaN(parsed.getTime())) {
      return "";
    }
    return parsed.toISOString();
  }

  private normalizeCouponCode = (data: any): CouponCodeListItem => {
    const startsAt = this.normalizeDate(data?.startsAt ?? data?.starts_at);
    const endsAt = this.normalizeDate(data?.endsAt ?? data?.ends_at);
    const createdAt = this.normalizeDate(data?.createdAt ?? data?.created_at);
    const updatedAt = this.normalizeDate(data?.updatedAt ?? data?.updated_at);
    const normalizeUsageType = (value: any): CouponCodeUsageType =>
      value === "user" ? "user" : "global";
    const usageType = normalizeUsageType(
      data?.usageType ?? data?.usage_type ?? data?.usageScope ?? data?.usage_scope
    );

    const normalizeAssignedUser = (
      rawUser: any
    ): CouponCodeAssignedUser | null => {
      if (!rawUser || typeof rawUser !== "object") {
        return null;
      }
      const uid =
        typeof rawUser.uid === "string"
          ? rawUser.uid
          : rawUser.uid != null
          ? String(rawUser.uid)
          : null;
      const email =
        typeof rawUser.email === "string"
          ? rawUser.email
          : rawUser.email != null
          ? String(rawUser.email)
          : null;
      const firstName =
        typeof rawUser.firstName === "string"
          ? rawUser.firstName
          : rawUser.first_name && typeof rawUser.first_name === "string"
          ? rawUser.first_name
          : null;
      const lastName =
        typeof rawUser.lastName === "string"
          ? rawUser.lastName
          : rawUser.last_name && typeof rawUser.last_name === "string"
          ? rawUser.last_name
          : null;

      if (!uid && !email && !firstName && !lastName) {
        return null;
      }

      return {
        uid,
        email,
        firstName,
        lastName,
      };
    };

    const assignedUsersSource =
      data?.assignedUsers ??
      data?.assigned_users ??
      data?.assignedUser ??
      data?.assigned_user ??
      null;

    const normalizedAssignedUsersList = Array.isArray(assignedUsersSource)
      ? assignedUsersSource
          .map((item) => normalizeAssignedUser(item))
          .filter(
            (item): item is CouponCodeAssignedUser => item !== null
          )
      : (() => {
          const single = normalizeAssignedUser(assignedUsersSource);
          return single ? [single] : [];
        })();

    const normalizedAssignedUsers =
      normalizedAssignedUsersList.length > 0
        ? normalizedAssignedUsersList
        : null;

    const normalizeUid = (value: any): string | null => {
      if (typeof value === "string") {
        return value;
      }
      if (value === null || value === undefined) {
        return null;
      }
      return String(value);
    };

    const assignedUserUidSource =
      data?.assignedUserUid ?? data?.assigned_user_uid ?? null;

    const normalizedAssignedUserUidList = Array.isArray(assignedUserUidSource)
      ? assignedUserUidSource
          .map((item) => normalizeUid(item))
          .filter((uid): uid is string => Boolean(uid))
      : (() => {
          const single = normalizeUid(assignedUserUidSource);
          return single ? [single] : [];
        })();

    const fallbackAssignedUids =
      normalizedAssignedUsers?.map((user) => user.uid).filter(
        (uid): uid is string => Boolean(uid)
      ) ?? [];

    const assignedUserUid =
      normalizedAssignedUserUidList.length > 0
        ? normalizedAssignedUserUidList
        : fallbackAssignedUids.length > 0
        ? fallbackAssignedUids
        : null;

    const normalizeInstrumentId = (value: any): number | null => {
      if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
      }
      if (typeof value === "string") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    };

    const normalizeAssignedInstrument = (
      rawInstrument: any
    ): CouponCodeAssignedInstrument | null => {
      if (rawInstrument === null || rawInstrument === undefined) {
        return null;
      }

      if (typeof rawInstrument === "object" && !Array.isArray(rawInstrument)) {
        const id = normalizeInstrumentId(
          rawInstrument.id ??
            rawInstrument.instrumentId ??
            rawInstrument.instrument_id ??
            rawInstrument.instrument?.id ??
            null
        );
        if (id === null) {
          return null;
        }
        const rawTitle =
          rawInstrument.title ??
          rawInstrument.instrumentTitle ??
          rawInstrument.instrument_title ??
          rawInstrument.instrument?.title ??
          "";
        const title =
          typeof rawTitle === "string"
            ? rawTitle
            : rawTitle !== null && rawTitle !== undefined
            ? String(rawTitle)
            : "";
        return {
          id,
          title: title.trim(),
        };
      }

      const id = normalizeInstrumentId(rawInstrument);
      if (id === null) {
        return null;
      }
      return {
        id,
        title: "",
      };
    };

    const assignedInstrumentIdSource =
      data?.assignedInstrumentId ??
      data?.assigned_instrument_id ??
      null;

    const normalizedAssignedInstrumentIds = Array.isArray(
      assignedInstrumentIdSource
    )
      ? assignedInstrumentIdSource
          .map((item) => normalizeInstrumentId(item))
          .filter(
            (id): id is number => typeof id === "number" && Number.isFinite(id)
          )
      : (() => {
          const single = normalizeInstrumentId(assignedInstrumentIdSource);
          return single !== null ? [single] : [];
        })();

    const assignedInstrumentSource =
      data?.assignedInstrument ?? data?.assigned_instrument ?? null;

    const normalizedAssignedInstrumentsList = Array.isArray(
      assignedInstrumentSource
    )
      ? assignedInstrumentSource
          .map((item) => normalizeAssignedInstrument(item))
          .filter(
            (
              instrument
            ): instrument is CouponCodeAssignedInstrument =>
              instrument !== null
          )
      : (() => {
          const single = normalizeAssignedInstrument(assignedInstrumentSource);
          return single ? [single] : [];
        })();

    const fallbackAssignedInstrumentIds =
      normalizedAssignedInstrumentsList.map((instrument) => instrument.id);

    const assignedInstrumentId =
      normalizedAssignedInstrumentIds.length > 0
        ? normalizedAssignedInstrumentIds
        : fallbackAssignedInstrumentIds.length > 0
        ? fallbackAssignedInstrumentIds
        : null;

    const assignedInstruments =
      normalizedAssignedInstrumentsList.length > 0
        ? normalizedAssignedInstrumentsList
        : null;

    return {
      id: data?.id ?? "",
      couponId: data?.couponId ?? data?.coupon_id ?? "",
      code: data?.code ?? "",
      assignedUserUid,
      assignedInstrumentId,
      assignedUsers: normalizedAssignedUsers,
      assignedInstruments,
      usageLimit:
        data?.usageLimit !== undefined
          ? data.usageLimit
          : data?.usage_limit ?? null,
      usageType,
      redemptionCount: Number(
        data?.redemptionCount ?? data?.redemption_count ?? 0
      ),
      startsAt: startsAt || null,
      endsAt: endsAt || null,
      active:
        typeof data?.active === "boolean"
          ? data.active
          : data?.active === "true" || data?.active === 1,
      createdAt: createdAt || "",
      updatedAt: updatedAt || "",
    };
  };

  /**
   * Runs `operation`, retrying it at most once with a refreshed token. The retry
   * calls `operation` again instead of re-entering this helper, so a route that
   * refuses permanently — the coupon endpoints answer 403 to anyone without the
   * ADMIN role — costs two requests rather than looping until the session dies.
   *
   * @throws the error the route reported, when the refresh itself fails.
   */
  private async withAuth<T>(
    accessToken: string,
    operation: (token: string) => Promise<T>
  ): Promise<T> {
    try {
      return await operation(accessToken);
    } catch (error: any) {
      if (!this.isAuthorizationError(error)) {
        throw error;
      }
      const tokens = await this.refreshTokens();
      if (!tokens) {
        throw error;
      }
      return operation(tokens.accessToken);
    }
  }

  private isAuthorizationError(error: any): boolean {
    const status = error?.response?.status;
    return status === 401 || status === 403;
  }

  private extractErrorMessage(error: any, fallback: string): string {
    if (Array.isArray(error?.response?.data?.errors)) {
      const [firstError] = error.response.data.errors;
      if (typeof firstError === "string") {
        return firstError;
      }
      if (firstError?.message) {
        return firstError.message;
      }
    }
    return (
      error?.response?.data?.message ??
      error?.response?.data?.error ??
      error?.message ??
      fallback
    );
  }

  private async refreshTokens(): Promise<
    { accessToken: string; refreshToken: string } | void
  > {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.shouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error) {
      console.error("Falha ao atualizar tokens", error);
      this.delegate.shouldLogout();
      return;
    }
  }
}
